import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Box, IconButton, useTheme, useMediaQuery, Theme } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';

interface HorizontalScrollContainerProps {
  children: React.ReactNode;
  /** Number of items - used to determine grid layout on larger screens */
  itemCount?: number;
}

const SCROLL_AMOUNT = 400;
const GRID_ITEM_WIDTH = '400px';

const HorizontalScrollContainer: React.FC<HorizontalScrollContainerProps> = ({
  children,
  itemCount = 0,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getScrollAvailability = (el: HTMLDivElement) => {
    const hasOverflowX = el.scrollWidth - el.clientWidth > 1;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    return {
      canScrollLeft: hasOverflowX && !atStart,
      canScrollRight: hasOverflowX && !atEnd,
    };
  };

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { canScrollLeft, canScrollRight } = getScrollAvailability(el);
    setCanScrollLeft(canScrollLeft);
    setCanScrollRight(canScrollRight);
  }, []);

  useEffect(() => {
    updateScrollState();

    const el = scrollRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    window.addEventListener('resize', updateScrollState);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { canScrollLeft, canScrollRight } = getScrollAvailability(el);
    setCanScrollLeft(canScrollLeft);
    setCanScrollRight(canScrollRight);
  };

  const scrollBack = () => {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const scrollForward = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const useGridLayout = itemCount > 0 && itemCount < 3;

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: '1200px' }}>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          display: { xs: 'flex', md: useGridLayout ? 'grid' : 'flex' },
          gridTemplateColumns: {
            md: useGridLayout
              ? itemCount === 2
                ? `repeat(2, ${GRID_ITEM_WIDTH})`
                : `repeat(${itemCount}, 1fr)`
              : undefined,
          },
          justifyContent: { md: useGridLayout ? 'center' : 'initial' },
          gap: 3,
          width: '100%',
          overflowX: 'auto',
          scrollSnapType: { xs: 'x mandatory', md: 'none' },
          scrollPaddingLeft: { xs: '32px', md: 0 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          pb: 2,
        }}
      >
        {/* Mobile left spacer */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            minWidth: '8px',
            flexShrink: 0,
          }}
        />
        {children}
        {/* Mobile right spacer */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            minWidth: '8px',
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Scroll arrow - left */}
      {!isSmallScreen && canScrollLeft && (
        <IconButton
          onClick={scrollBack}
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: theme.palette.background.default,
            border: '1px solid #D3D3D3',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1,
            '&:hover': {
              backgroundColor: theme.palette.background.default,
            },
          }}
        >
          <ArrowBack sx={{ color: theme.palette.text.secondary }} />
        </IconButton>
      )}

      {/* Scroll arrow - right */}
      {!isSmallScreen && canScrollRight && (
        <IconButton
          onClick={scrollForward}
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: theme.palette.background.default,
            border: '1px solid #D3D3D3',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1,
            '&:hover': {
              backgroundColor: theme.palette.background.default,
            },
          }}
        >
          <ArrowForward sx={{ color: theme.palette.text.secondary }} />
        </IconButton>
      )}
    </Box>
  );
};

export default HorizontalScrollContainer;
